'use client';

import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';

interface EarthIconProps {
    /**
     * 图标的宽度，默认为 200px
     */
    width?: number;
    /**
     * 图标的高度，默认为 200px
     */
    height?: number;
    /**
     * 动画持续时间（秒），默认为 0.3s
     */
    duration?: number;
    /**
     * 额外的 CSS 类名
     */
    className?: string;
    /**
     * 是否启用动画交互，默认为 true
     */
    enableAnimation?: boolean;
    /**
     * 是否使用 group 模式（通过父元素的 group-hover 触发），默认为 false
     */
    useGroupHover?: boolean;
    /**
     * 主体颜色（圆环和中心圆点），支持 CSS 颜色值或 Tailwind 类名
     */
    primaryColor?: string;
    /**
     * 动画元素颜色（上下移动的紫色元素），支持 CSS 颜色值或 Tailwind 类名
     */
    accentColor?: string;
    /**
     * 颜色主题预设：'default' | 'blue' | 'green' | 'red' | 'orange' | 'indigo'
     */
    colorTheme?:
        | 'default'
        | 'blue'
        | 'green'
        | 'red'
        | 'orange'
        | 'indigo'
        | 'custom';
}

/**
 * 地球图标组件，支持鼠标悬停时的动画效果
 * 当鼠标悬停时，上下两个紫色元素会向中心移动
 * 当鼠标离开时，元素回到初始位置
 */
const EarthIcon: React.FC<EarthIconProps> = ({
    width = 200,
    height = 200,
    duration = 0.3,
    className = '',
    enableAnimation = true,
    useGroupHover = false,
    primaryColor,
    accentColor,
    colorTheme = 'default',
}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const topElementRef = useRef<SVGPathElement>(null);
    const bottomElementRef = useRef<SVGPathElement>(null);
    const timelineRef = useRef<gsap.core.Timeline | null>(null);

    // 颜色主题配置
    const getThemeColors = () => {
        // 如果设置了自定义颜色，自动启用 custom 模式
        const effectiveTheme =
            primaryColor || accentColor ? 'custom' : colorTheme;

        const themes = {
            default: {
                primary: 'text-black dark:text-white',
                accent: '#7E4DF9', // 紫色
            },
            blue: {
                primary: 'text-blue-600 dark:text-blue-400',
                accent: '#3B82F6', // 蓝色
            },
            green: {
                primary: 'text-green-600 dark:text-green-400',
                accent: '#10B981', // 绿色
            },
            red: {
                primary: 'text-red-600 dark:text-red-400',
                accent: '#EF4444', // 红色
            },
            orange: {
                primary: 'text-orange-600 dark:text-orange-400',
                accent: '#F97316', // 橙色
            },
            indigo: {
                primary: 'text-indigo-600 dark:text-indigo-400',
                accent: '#6366F1', // 靛蓝色
            },
            custom: {
                primary: primaryColor || 'text-black dark:text-white',
                accent: accentColor || '#7E4DF9',
            },
        };

        return themes[effectiveTheme] || themes.default;
    };

    const themeColors = getThemeColors();

    useEffect(() => {
        if (
            !enableAnimation ||
            !svgRef.current ||
            !topElementRef.current ||
            !bottomElementRef.current
        ) {
            return;
        }

        const svg = svgRef.current;
        const topElement = topElementRef.current;
        const bottomElement = bottomElementRef.current;

        // 创建 GSAP 时间轴
        const tl = gsap.timeline({ paused: true });
        timelineRef.current = tl;

        // 设置初始状态
        gsap.set(topElement, { y: -7 });
        gsap.set(bottomElement, { y: 7 });

        // 定义动画序列：向中心移动
        tl.to([topElement, bottomElement], {
            y: 0,
            duration: duration,
            ease: 'power2.out',
        });

        // 鼠标进入事件：播放动画
        const handleMouseEnter = () => {
            tl.play();
        };

        // 鼠标离开事件：反向播放动画
        const handleMouseLeave = () => {
            tl.reverse();
        };

        if (useGroupHover) {
            // 使用 group 模式：监听父元素的 hover 状态
            const groupElement = svg.closest('.group');
            if (groupElement) {
                groupElement.addEventListener('mouseenter', handleMouseEnter);
                groupElement.addEventListener('mouseleave', handleMouseLeave);

                // 清理函数
                return () => {
                    groupElement.removeEventListener(
                        'mouseenter',
                        handleMouseEnter,
                    );
                    groupElement.removeEventListener(
                        'mouseleave',
                        handleMouseLeave,
                    );
                    tl.kill();
                };
            }
        } else {
            // 默认模式：监听 SVG 自身的 hover 状态
            svg.addEventListener('mouseenter', handleMouseEnter);
            svg.addEventListener('mouseleave', handleMouseLeave);

            // 清理函数
            return () => {
                svg.removeEventListener('mouseenter', handleMouseEnter);
                svg.removeEventListener('mouseleave', handleMouseLeave);
                tl.kill();
            };
        }
    }, [duration, enableAnimation, useGroupHover]);

    return (
        <div
            className={`inline-flex items-center justify-center ${className}`}
            style={{ width, height }}
        >
            <svg
                ref={svgRef}
                width={width}
                height={height}
                viewBox="0 0 18 18"
                xmlns="http://www.w3.org/2000/svg"
            >
                <g clipPath="url(#clip0_923_722)">
                    {/* 背景圆环 */}
                    <path
                        d="M9 0C11.3845 0 13.335 3.7094 13.4893 8.40039H18V9.59961H13.4893C13.335 14.2906 11.3845 18 9 18C6.61547 18 4.66503 14.2906 4.51074 9.59961H0V8.40039H4.51074C4.66503 3.7094 6.61547 0 9 0ZM5.71191 9.59961C5.78553 11.6902 6.23866 13.5213 6.8916 14.8271C7.64216 16.3283 8.45675 16.7998 9 16.7998C9.54325 16.7998 10.3578 16.3283 11.1084 14.8271C11.7613 13.5213 12.2145 11.6902 12.2881 9.59961H5.71191ZM9 1.2002C8.45675 1.2002 7.64216 1.67174 6.8916 3.17285C6.23866 4.47873 5.78553 6.30978 5.71191 8.40039H12.2881C12.2145 6.30978 11.7613 4.47873 11.1084 3.17285C10.3578 1.67174 9.54325 1.2002 9 1.2002Z"
                        fill={
                            themeColors.primary.startsWith('#')
                                ? themeColors.primary
                                : 'currentColor'
                        }
                        className={
                            themeColors.primary.startsWith('#')
                                ? ''
                                : themeColors.primary
                        }
                    />

                    {/* 下方动画元素 */}
                    <path
                        ref={bottomElementRef}
                        d="M16.7998 21C16.7998 16.6922 13.3078 13.2002 9 13.2002C4.69218 13.2002 1.2002 16.6922 1.2002 21C1.2002 25.3078 4.69218 28.7998 9 28.7998V30C4.02944 30 0 25.9706 0 21C0 16.0294 4.02944 12 9 12C13.9706 12 18 16.0294 18 21C18 25.9706 13.9706 30 9 30V28.7998C13.3078 28.7998 16.7998 25.3078 16.7998 21Z"
                        fill={themeColors.accent}
                    />

                    {/* 上方动画元素 */}
                    <path
                        ref={topElementRef}
                        d="M16.7998 -3C16.7998 -7.30782 13.3078 -10.7998 9 -10.7998C4.69218 -10.7998 1.2002 -7.30782 1.2002 -3C1.2002 1.30782 4.69218 4.7998 9 4.7998V6C4.02944 6 0 1.97056 0 -3C0 -7.97056 4.02944 -12 9 -12C13.9706 -12 18 -7.97056 18 -3C18 1.97056 13.9706 6 9 6V4.7998C13.3078 4.7998 16.7998 1.30782 16.7998 -3Z"
                        fill={themeColors.accent}
                    />

                    {/* 中心圆点 */}
                    <path
                        d="M9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9C0 4.02944 4.02944 0 9 0ZM9 1.2002C4.69218 1.2002 1.2002 4.69218 1.2002 9C1.2002 13.3078 4.69218 16.7998 9 16.7998C13.3078 16.7998 16.7998 13.3078 16.7998 9C16.7998 4.69218 13.3078 1.2002 9 1.2002Z"
                        fill={
                            themeColors.primary.startsWith('#')
                                ? themeColors.primary
                                : 'currentColor'
                        }
                        className={
                            themeColors.primary.startsWith('#')
                                ? ''
                                : themeColors.primary
                        }
                    />
                </g>

                <defs>
                    <clipPath id="clip0_923_722">
                        <rect width="18" height="18" rx="9" fill="white" />
                    </clipPath>
                </defs>
            </svg>
        </div>
    );
};

export default EarthIcon;
